import type { replayIntegration } from "@sentry/react";

type ReplayOptions = NonNullable<Parameters<typeof replayIntegration>[0]>;
type RecordingEvent = Parameters<NonNullable<ReplayOptions["beforeAddRecordingEvent"]>>[0];

/**
 * API paths whose request or response bodies carry credentials, so replays never record them,
 * even after the key scrub below: login and password flows (`/auth/*`, including the
 * form-encoded login body, which is a string the key scrub cannot see into), the endpoints that
 * hand back the shared operator password, and the integrations that exchange OAuth tokens.
 */
export const REPLAY_NETWORK_DENY_PATHS = [
	"/auth/",
	"/team-members/create",
	"/team-members/set-password",
	"/proagrica/",
	"/agrian/",
];

/** A key whose value is masked wherever it appears in a captured body (case-insensitive substring). */
const SENSITIVE_KEY = /password|token|secret|authorization|cookie|api_?key|credential/i;
const REDACTED = "[Filtered]";

/** Recursively mask sensitive keys. A string body is masked whole when it mentions one, since its
 *  shape (form-encoded, truncated JSON) is unknown. */
export function scrubNetworkBody(body: unknown): unknown {
	if (typeof body === "string") return SENSITIVE_KEY.test(body) ? REDACTED : body;
	if (Array.isArray(body)) return body.map(scrubNetworkBody);
	if (body && typeof body === "object") {
		return Object.fromEntries(
			Object.entries(body).map(([key, value]) => [key, SENSITIVE_KEY.test(key) ? REDACTED : scrubNetworkBody(value)]),
		);
	}
	return body;
}

/** `beforeAddRecordingEvent`: scrub the bodies of captured fetch/xhr spans, pass everything else through. */
export function scrubReplayNetworkEvent(event: RecordingEvent): RecordingEvent {
	const data = (event as { data?: { tag?: string; payload?: { op?: string; data?: Record<string, any> } } }).data;
	const op = data?.payload?.op;
	if (data?.tag !== "performanceSpan" || (op !== "resource.fetch" && op !== "resource.xhr")) return event;
	for (const side of ["request", "response"] as const) {
		const detail = data.payload?.data?.[side];
		if (detail && "body" in detail) detail.body = scrubNetworkBody(detail.body);
	}
	return event;
}

/**
 * Replay options that record request/response bodies for Portal API calls, so a replay of a
 * failed save shows what was sent and what the API answered. Headers stay at the SDK defaults
 * (Content-Type, Content-Length, Accept): `Authorization` is never captured.
 */
export function replayNetworkOptions(apiUrl: string): Partial<ReplayOptions> {
	return {
		networkDetailAllowUrls: [apiUrl],
		networkDetailDenyUrls: REPLAY_NETWORK_DENY_PATHS,
		beforeAddRecordingEvent: scrubReplayNetworkEvent,
	};
}
